import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-toastify';

import { META_COLLECT_API } from '../../../api';
import {
  Configuration,
  IConfigurationoDTO,
} from '../../../types/Configuration';
import { useSigninContext } from '../../AuthProviderPages/Signin';
import { usePageContext } from '../../usePage';

interface IContextProps {
  children: ReactNode;
}

type OnCreateNewConfiguration = {
  config: {
    DFDISTRIBUIR: string;
    DFREGUAATRAS: string;
    DFQUALIDADE: string;
    DFEXAMESENSORIAL: string;
    DFEXAMEALIZAROL: string;
    DFPASSOUNOALIZAROL: string;
    DFAMOSTRADEQUALIDE: string;
    DFFOTOSDEAMOSTRA: string;
    DFFOTOSDATEMPERATURA: string;
    DFAMOSTRA: string;
    DFLACRE: string;
  };
};

interface IContext {
  loadingConfigurations: boolean;
  configurations: Configuration[];
  loadConfigurationsAction: () => void;
  openCreateNewConfiguration: boolean;
  openAndCloseCreateNewConfiguration: () => void;
  onCreateNewConfiguration: ({ config }: OnCreateNewConfiguration) => void;
  editOneConfiguration: boolean;
  setEditOneConfiguration: Dispatch<SetStateAction<boolean>>;
  selectedConfiguration: Configuration | null;
  setSelectedConfiguration: Dispatch<SetStateAction<Configuration | null>>;
  handleEditOneAppConfiguration: (config: IConfigurationoDTO) => void;
}

const Context = createContext({} as IContext);

const ConfigurationContextProvider = ({ children }: IContextProps) => {
  const { path } = useSigninContext();
  const { selectedPage } = usePageContext();
  const [loadingConfigurations, setLoadingConfigurations] = useState(true);
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [openCreateNewConfiguration, setOpenCreateNewConfiguration] =
    useState(false);
  const [editOneConfiguration, setEditOneConfiguration] =
    useState<boolean>(false);
  const [selectedConfiguration, setSelectedConfiguration] =
    useState<Configuration | null>(null);

  const loadConfigurationsAction = () => {
    META_COLLECT_API.get('/configuracao/panel', {
      headers: {
        path,
      },
    })
      .then(({ data }) => {
        setConfigurations(data.configuracoes);
        setLoadingConfigurations(false);
      })
      .catch(() => {
        setLoadingConfigurations(false);
      });
  };

  const openAndCloseCreateNewConfiguration = () => {
    setOpenCreateNewConfiguration(!openCreateNewConfiguration);
  };

  const onCreateNewConfiguration = ({ config }: OnCreateNewConfiguration) => {
    setLoadingConfigurations(true);
    openAndCloseCreateNewConfiguration();
    META_COLLECT_API.patch('/configuracao/panel/atualizar', config, {
      headers: {
        path,
      },
    })
      .then(() => {
        loadConfigurationsAction();
      })
      .catch(() => {
        setLoadingConfigurations(false);
      });
    loadConfigurationsAction();
  };

  useEffect(() => {
    if (selectedPage.option !== 'configuracoes') {
      setOpenCreateNewConfiguration(false);
      setEditOneConfiguration(false);
      setSelectedConfiguration(null);
    }
  }, [selectedPage]);

  const handleEditOneAppConfiguration = (config: IConfigurationoDTO) => {
    if (selectedConfiguration) {
      META_COLLECT_API.patch(
        '/configuracao/panel/atualizar-um',
        { ...config, DFICARRETEIRO: selectedConfiguration.DFIDCARRETEIRO },
        {
          headers: {
            path,
          },
        },
      )
        .then(() => {
          setSelectedConfiguration(null);
          setEditOneConfiguration(false);
          loadConfigurationsAction();
          toast.success(
            `As configurações do carreteiro ${selectedConfiguration.DFNOMECARRETEIRO} foram editas com sucesso!`,
          );
        })
        .catch(_ => {
          toast.error(
            `Falha ao editar configurações do carreteiro ${selectedConfiguration.DFNOMECARRETEIRO}`,
          );
        });
    }
  };

  return (
    <Context.Provider
      value={{
        loadingConfigurations,
        loadConfigurationsAction,
        configurations,
        openCreateNewConfiguration,
        openAndCloseCreateNewConfiguration,
        onCreateNewConfiguration,
        editOneConfiguration,
        setEditOneConfiguration,
        selectedConfiguration,
        setSelectedConfiguration,
        handleEditOneAppConfiguration,
      }}
    >
      {children}
    </Context.Provider>
  );
};

function useConfiguraionContext() {
  const context = useContext(Context);
  return context;
}

export { ConfigurationContextProvider, useConfiguraionContext };
